import { Injectable } from '@angular/core';
import { Entity, EntityQuery, Predicate } from 'breeze-client';
import {
    Attachment,
    CustomerMixStrengthLink,
    CustomerProductPriceOverride,
    CustomerTaxCode,
    EmailMessage,
    EmailMessageCustomerLink,
    EmailMessageInvoiceLink,
    InvoiceLine,
    InvoicePaymentMethod,
    InvoiceService,
    InvoiceStatus,
    InvoiceType,
    JournalEntry,
    LoadAdditive,
    MixStrength,
    Payment,
    PaymentCredit,
    PaymentLine,
    PrintRequest,
    PrintRequestInvoice,
    Product,
    ProductCategory,
    ProductCustomerTypeLink,
    ProductLocation,
    ProductMaterial,
    ProductUnitOfMeasure,
    QuickBooksAccount,
    QuickBooksTaskResponse,
    Quote,
    QuoteLine,
    QuoteStatus,
    Setting,
    Statement,
    TicketAttachment,
    User,
    UserRole,
    Vendor,
} from '../model/entity-model';
import { CustomerRepository } from './customer.repository';
import { Entities } from './entities';
import { EntityManagerProvider } from './entity-manager-provider';
import { InvoiceRepository } from './invoice.repository';
import { Repository } from './repository';
import { UnitOfWork } from './unit-of-work';
import { FileService } from '../file.service';

@Injectable()
export class BrewersUnitOfWork extends UnitOfWork {
    attachments: Repository<Attachment>;
    customers: CustomerRepository;
    customerTaxCodes: Repository<CustomerTaxCode>;
    customerMixStrengthLinks: Repository<CustomerMixStrengthLink>;
    customerProductPriceOverrides: Repository<CustomerProductPriceOverride>;
    emailMessages: Repository<EmailMessage>;
    emailMessageInvoiceLinks: Repository<EmailMessageInvoiceLink>;
    emailMessageCustomerLinks: Repository<EmailMessageCustomerLink>;
    invoices: InvoiceRepository;
    invoiceLines: Repository<InvoiceLine>;
    invoicePaymentMethods: Repository<InvoicePaymentMethod>;
    invoiceServices: Repository<InvoiceService>;
    invoiceStatuses: Repository<InvoiceStatus>;
    invoiceTypes: Repository<InvoiceType>;
    journalEntries: Repository<JournalEntry>;
    loads: Repository<Entity>;
    loadAdditives: Repository<LoadAdditive>;
    mixStrengths: Repository<MixStrength>;
    payments: Repository<Payment>;
    paymentCredits: Repository<PaymentCredit>;
    paymentLines: Repository<PaymentLine>;
    printRequests: Repository<PrintRequest>;
    printRequestInvoices: Repository<PrintRequestInvoice>;
    productCategories: Repository<ProductCategory>;
    productCustomerTypeLinks: Repository<ProductCustomerTypeLink>;
    productLocations: Repository<ProductLocation>;
    productMaterials: Repository<ProductMaterial>;
    productUnitsOfMeasure: Repository<ProductUnitOfMeasure>;
    products: Repository<Product>;
    projects: Repository<Entity>;
    quickBooksAccounts: Repository<QuickBooksAccount>;
    quotes: Repository<Quote>;
    quoteLines: Repository<QuoteLine>;
    quoteStatuses: Repository<QuoteStatus>;
    roles: Repository<Entity>;
    statements: Repository<Statement>;
    ticketAttachments: Repository<TicketAttachment>;
    timeEntries: Repository<Entity>;
    users: Repository<Entity>;
    userRoles: Repository<UserRole>;
    vendors: Repository<Vendor>;

    lookups: any;

    constructor(emProvider: EntityManagerProvider) {
        super(emProvider);

        this.lookups = Entities.lookups;
        emProvider.prepare().then((lookups) => {
            this.lookups = lookups;
        });

        this.attachments = this.createRepository<Attachment>('Attachment', 'attachments');
        this.customers = new CustomerRepository(this.manager);
        this.customerTaxCodes = this.createRepository<CustomerTaxCode>('CustomerTaxCode', 'customerTaxCodes');
        this.customerMixStrengthLinks = this.createRepository<CustomerMixStrengthLink>('CustomerMixStrengthLink', 'customerMixStrengthLinks');
        this.customerProductPriceOverrides = this.createRepository<CustomerProductPriceOverride>('CustomerProductPriceOverride', 'customerProductPriceOverrides');
        this.emailMessages = this.createRepository<EmailMessage>('EmailMessage', 'emailMessages');
        this.emailMessageInvoiceLinks = this.createRepository<EmailMessageInvoiceLink>('EmailMessageInvoiceLink', 'emailMessageInvoiceLinks');
        this.emailMessageCustomerLinks = this.createRepository<EmailMessageCustomerLink>('EmailMessageCustomerLink', 'emailMessageCustomerLinks');
        this.invoices = new InvoiceRepository(this.manager);
        this.invoiceLines = this.createRepository<InvoiceLine>('InvoiceLine', 'invoiceLines');
        this.invoicePaymentMethods = this.createRepository<InvoicePaymentMethod>('InvoicePaymentMethod', 'invoicePaymentMethods');
        this.invoiceServices = this.createRepository<InvoiceService>('InvoiceService', 'invoiceServices');
        this.invoiceStatuses = this.createRepository<InvoiceStatus>('InvoiceStatus', 'invoiceStatuses');
        this.invoiceTypes = this.createRepository<InvoiceType>('InvoiceType', 'invoiceTypes');
        this.journalEntries = this.createRepository<JournalEntry>('JournalEntry', 'journalEntries');
        this.loads = this.createRepository<Entity>('Load', 'loads');
        this.loadAdditives = this.createRepository<LoadAdditive>('LoadAdditive', 'loadAdditives');
        this.mixStrengths = this.createRepository<MixStrength>('MixStrength', 'mixStrengths');
        this.payments = this.createRepository<Payment>('Payment', 'payments');
        this.paymentCredits = this.createRepository<PaymentCredit>('PaymentCredit', 'paymentCredits');
        this.paymentLines = this.createRepository<PaymentLine>('PaymentLine', 'paymentLines');
        this.printRequests = this.createRepository<PrintRequest>('PrintRequest', 'printRequests');
        this.printRequestInvoices = this.createRepository<PrintRequestInvoice>('PrintRequestInvoice', 'printRequestInvoices');
        this.productCategories = this.createRepository<ProductCategory>('ProductCategory', 'productCategories');
        this.productCustomerTypeLinks = this.createRepository<ProductCustomerTypeLink>('ProductCustomerTypeLink', 'productCustomerTypeLinks');
        this.productLocations = this.createRepository<ProductLocation>('ProductLocation', 'productLocations');
        this.productMaterials = this.createRepository<ProductMaterial>('ProductMaterial', 'productMaterials');
        this.productUnitsOfMeasure = this.createRepository<ProductUnitOfMeasure>('ProductUnitOfMeasure', 'productUnitsOfMeasure');
        this.products = this.createRepository<Product>('Product', 'products');
        this.projects = this.createRepository<Entity>('Project', 'projects');
        this.quickBooksAccounts = this.createRepository<QuickBooksAccount>('QuickBooksAccount', 'quickBooksAccounts');
        this.quotes = this.createRepository<Quote>('Quote', 'quotes');
        this.quoteLines = this.createRepository<QuoteLine>('QuoteLine', 'quoteLines');
        this.quoteStatuses = this.createRepository<QuoteStatus>('QuoteStatus', 'quoteStatuses');
        this.roles = this.createRepository<Entity>('Role', 'roles');
        this.statements = this.createRepository<Statement>('Statement', 'statements');
        this.ticketAttachments = this.createRepository<TicketAttachment>('TicketAttachment', 'ticketAttachments');
        this.timeEntries = this.createRepository<Entity>('TimeEntry', 'timeEntries');
        this.users = this.createRepository<Entity>('User', 'users');
        this.userRoles = this.createRepository<UserRole>('UserRole', 'userRoles');
        this.vendors = this.createRepository<Vendor>('Vendor', 'vendors');
    }

    async getSettingsByName(name: string) {
        const query = EntityQuery.from('Settings').where('name', '==', name);
        const data = await this.manager.executeQuery(query);
        return data.results[0] as Setting;
    }

    async getUsers(expand?: string) {
        const query = EntityQuery.from('Users').where('active', '==', true).expand(expand).orderBy('firstName');
        const data = await this.manager.executeQuery(query);
        return data.results as User[];
    }

    async getSettings() {
        const query = EntityQuery.from('Settings');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeEntries(options?: any) {
        let predicate = new Predicate('deleted', '==', null);
        if (!!options) {
            if (options.hideApproved) {
                predicate = predicate.and('approved', '==', null);
            }
        }
        let query = EntityQuery.from('TimeEntries').where(predicate).expand('user').orderBy('entryDate, id');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeByUserId(userId: number) {
        let predicate = new Predicate('deleted', '==', null).and('approved', '==', null).and('userId', '==', userId);
        const query = EntityQuery.from('TimeEntries').where(predicate).orderBy('entryDate, id');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getTimeByWeekForUser(userId: number) {
        let predicate = new Predicate('deleted', '==', null).and('userId', '==', userId);
        const query = EntityQuery.from('GetPreviousWeeksTime').withParameters({ userId: userId });
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getSimpleTimeOverview() {
        const query = EntityQuery.from('GetSimpleTimeOverview');
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getRecentTicketAttachments(take: number) {
        const query = EntityQuery.from('TicketAttachments').where('deleted', '==', null).orderBy('created desc').take(take);
        const data = await this.manager.executeQuery(query);
        return data.results as TicketAttachment[];
    }

    async getProducts(materialId?: number, expand?: string | string[], select?: string) {
        let query = EntityQuery.from('Products').where('deleted', '==', null).orderBy('partNumber');
        if (materialId) query = query.where('materialId', '==', materialId);
        if (expand) query = query.expand(expand);
        if (select) query = query.select(select);
        const data = await this.manager.executeQuery(query);
        return data.results as Product[];
    }

    async getLookupProducts() {
        const products = (await this.getProducts(null, null, 'id, partNumber')) as any[];
        return products.map(product => {
            return {
                id: product.id,
                name: product.partNumber
            }
        });
    }

    async getDeliveryProducts(excludedId: number) {
        let predicate = new Predicate('deleted', '==', null).and('materialId', '==', ProductMaterial.Materials.Delivery).and('id', '!=', excludedId)
        let query = EntityQuery.from('Products').where(predicate).orderBy('partNumber');
        const data = await this.manager.executeQuery(query);
        return data.results as Product[];
    }

    async getProductPartNumbers(excludeId: number) {
        const predicate = new Predicate('deleted', '==', null).and('id', '!=', excludeId);
        const query = EntityQuery.from('Products').where(predicate).select('partNumber');
        const data = await this.manager.executeQuery(query);
        return data.results?.map((p) => p.partNumber) ?? ([] as string[]);
    }

    async getAttachments(mode?: number, id?: number, includeDeleted: boolean = false) {
        let query = EntityQuery.from('Attachments').orderByDesc('created');
        if (!includeDeleted) query = query.where('deleted', '==', null);
        if (mode === FileService.Mode.Invoice) query = query.where('invoiceId', '==', id);
        else if (mode === FileService.Mode.EmailMessage) query = query.where('emailMessageId', '==', id);
        else if (mode === FileService.Mode.Customer) query = query.where('customerId', '==', id);
        const data = await this.manager.executeQuery(query);
        return data.results;
    }

    async getStatements(includeDeleted: boolean = false) {
        let query = EntityQuery.from('Statements').orderByDesc('created');
        if (!includeDeleted) query = query.where('deleted', '==', null);
        const data = await this.manager.executeQuery(query);
        return data.results as Statement[];
    }

    async getCustomerUnixKeys(excludeId: number) {
        const predicate = new Predicate('deleted', '==', null).and('id', '!=', excludeId);
        const query = EntityQuery.from('Customers').where(predicate).select('customerUnixKey');
        const data = await this.manager.executeQuery(query);
        return data.results?.map((p) => p.customerUnixKey) ?? ([] as string[]);
    }

    async getCustomerMixStrengthLinks(customerId: number, productId?: number, mixStrengthId?: number) {
        let predicate = new Predicate('deleted', '==', null).and('mixStrength.deleted', '==', null).and('customerId', '==', customerId);
        if (productId) predicate = predicate.and('mixStrength.products', 'any', 'id', '==', productId);
        if (mixStrengthId) predicate = predicate.and('mixStrengthId', '==', mixStrengthId);
        const query = EntityQuery.from('CustomerMixStrengthLinks').where(predicate).orderBy('mixStrength.displayOrder, mixStrength.name');
        const data = await this.manager.executeQuery(query);
        return data.results as CustomerMixStrengthLink[];
    }

    async getCustomerProductPriceOverrides(customerId: number) {
        const predicate = new Predicate('deleted', '==', null).and('customerId', '==', customerId);
        const query = EntityQuery.from('CustomerProductPriceOverrides').where(predicate).orderBy('product.description');
        const data = await this.manager.executeQuery(query);
        return data.results as CustomerProductPriceOverride[];
    }

    async getCustomerIdsWithActiveProductPriceOverrides() {
        const today = new Date();
        const activePredicate = new Predicate('deleted', '==', null).and(new Predicate('endDate', '==', null).or('endDate', '>=', today));
        const query = EntityQuery.from('CustomerProductPriceOverrides').where(activePredicate).select('customerId');
        const data = await this.manager.executeQuery(query);
        return new Set<number>(data.results?.map((p: any) => p.customerId) ?? []);
    }

    async getCustomerOpenBalances() {
        const query = EntityQuery.from('GetCustomerOpenBalances');
        const data = await this.manager.executeQuery(query);
        return new Map<number, number>(data.results?.map((r: any) => [r.customerId, r.openBalance]) ?? []);
    }

    async getDataToExport(breezeController: string, expand?: string | string[]) {
        let predicate = new Predicate('deleted', '==', null);
        const orPredicate = new Predicate('quickBooksId', '==', null).or('quickBooksLastExport', '==', null);
        predicate = predicate.and(orPredicate);

        let query = EntityQuery.from(breezeController).where(predicate);
        if (expand) query = query.expand(expand);
        const data = await this.manager.executeQuery(query);
        return data.results as any[];
    }

    async getQBTaskResponses() {
        const query = EntityQuery.from('QuickBooksTaskResponses')
            .orderByDesc('id')
            .expand('quickBooksTask, customer, invoice.customer, vendor, payment.customer')
            .take(500);
        const data = await this.manager.executeQuery(query);
        return data.results as QuickBooksTaskResponse[];
    }

    async getQuickBooksAccounts(select?: string | string[]) {
        let query = EntityQuery.from('QuickBooksAccounts').orderBy('quickBooksAccountNumber, name')
        if (select) query = query.select(select);
        const data = await this.manager.executeQuery(query);
        return data.results as QuickBooksAccount[];
    }

    async getLatestCustomerTypePrices(productId: number) {
        const query = EntityQuery.from('GetLatestCustomerTypePrices').withParameters({ productId });
        const data = await this.manager.executeQuery(query);
        return data.results as ProductCustomerTypeLink[];
    }
}
